/**
 * « Planifier une action » — le point d'entrée unique des suites à donner à
 * une fiche : rendez-vous, démo, vidéo de démo, email, appel, suivi.
 *
 * Il remplace la « Nouvelle tâche » et le formulaire d'email qui vivaient
 * séparément : le commercial choisit D'ABORD ce qu'il veut faire, la modale ne
 * montre ensuite que ce qui sert. Un email ou une vidéo de démo se déposent
 * soit tout de suite (envoi réel, journalisé en activité), soit plus tard (une
 * action planifiée qui garde le brouillon). Tout le reste devient une action
 * datée, qui remonte dans l'onglet Actions, la page Tâches et le calendrier.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, MonitorPlay, Video, Mail, PhoneCall, RotateCcw, CheckSquare, X, Link2,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import Modal from "@/crm/components/Modal";
import { ACTION_TYPES, isEmailAction } from "@/crm/actions";

const ICONS = {
  MEETING: Users, DEMO: MonitorPlay, VIDEO: Video, EMAIL: Mail,
  CALL: PhoneCall, FOLLOW_UP: RotateCcw, OTHER: CheckSquare,
};
const FIELD = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-500";
const LABEL = "block font-mono text-[10.5px] tracking-[.1em] uppercase text-slate-400 mb-1";
const BTN_PRIMARY = "px-3.5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const DONE = {
  MEETING: "Rendez-vous planifié", DEMO: "Démo planifiée", VIDEO: "Envoi de la vidéo planifié",
  EMAIL: "Envoi de l'email planifié", CALL: "Appel planifié", FOLLOW_UP: "Suivi planifié",
  OTHER: "Action planifiée",
};

/** Titre proposé pour chaque type — remplacé dès que le commercial en écrit un. */
export const defaultTitle = (kind, name) => ({
  MEETING: `Rendez-vous avec ${name}`, DEMO: `Démo pour ${name}`,
  VIDEO: `Envoyer la vidéo de démo à ${name}`, EMAIL: `Email à ${name}`,
  CALL: `Appeler ${name}`, FOLLOW_UP: `Relancer ${name}`, OTHER: "",
}[kind] ?? "");

// Le corps proposé pour la vidéo ne contient pas le lien : il est ajouté à
// l'envoi (`withVideo`), donc toujours celui affiché dans le champ, même corrigé.
const VIDEO_MESSAGE = "Bonjour {nom},\n\nComme convenu, voici notre vidéo de démonstration. "
  + "Je reste disponible pour en discuter de vive voix.\n\n{commercial}";
export const withVideo = (message, url) => `${(message || "").trimEnd()}\n\nVidéo de démonstration : ${url}`;

export default function PlanActionModal({ company, suggestedEmails = [], onClose, onSaved,
                                          initialKind = "MEETING" }) {
  // `initialKind` : les raccourcis du panneau d'appel (RDV, démo, rappel)
  // ouvrent la modale déjà positionnée — même formulaire, un clic de moins.
  const { user } = useAuth();
  const [kind, setKind] = useState(initialKind);
  const [title, setTitle] = useState(defaultTitle(initialKind, company.nom));
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [priority, setPriority] = useState("medium");
  const [notes, setNotes] = useState("");
  // Email / vidéo : tout de suite, ou planifié. Une fiche en opposition ne
  // peut plus recevoir d'email — on ne propose alors que la planification.
  const [mode, setMode] = useState(company.excluded ? "later" : "now");
  const [dest, setDest] = useState(company.email || "");
  const [objet, setObjet] = useState("");
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [preview, setPreview] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoConfigured, setVideoConfigured] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/email-templates").then(({ data }) => setTemplates(Array.isArray(data) ? data : [])).catch(() => {});
    // Le lien de la vidéo de démo est un réglage d'équipe : le commercial n'a
    // pas à le retrouver à chaque envoi, mais peut le remplacer pour un cas.
    api.get("/settings").then(({ data }) => {
      const url = data?.demo_video_url || "";
      setVideoUrl(url); setVideoConfigured(Boolean(url));
    }).catch(() => {});
  }, []);

  const email = isEmailAction(kind);
  const sendNow = email && mode === "now";

  const pickKind = (next) => {
    // Le titre suit le type tant qu'il n'a pas été écrit à la main.
    setTitle((t) => (!t || t === defaultTitle(kind, company.nom) ? defaultTitle(next, company.nom) : t));
    if (next === "VIDEO") {
      setObjet((o) => o || `Vidéo de démonstration — ${company.nom}`);
      setMessage((m) => m || VIDEO_MESSAGE);
    }
    setKind(next);
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setObjet(t.objet || ""); setMessage(t.corps || ""); }
  };

  const finalMessage = kind === "VIDEO" ? withVideo(message, videoUrl.trim() || "[lien de la vidéo]") : message;
  // Même substitution que le serveur (email_service.build_context), pour un
  // aperçu fidèle : {nom} vaut l'entreprise faute de contact désigné.
  const rendered = (text) => (text || "")
    .replace(/\{\s*(nom|entreprise|contact)\s*\}/g, company.nom || "")
    .replace(/\{\s*ville\s*\}/g, company.ville || "")
    .replace(/\{\s*email\s*\}/g, company.email || "")
    .replace(/\{\s*telephone\s*\}/g, company.telephone || "")
    .replace(/\{\s*commercial\s*\}/g, user?.name || "");

  const send = async () => {
    const to = dest.trim();
    if (!EMAIL_RE.test(to)) return toast.error("Destinataire invalide");
    if (!objet.trim()) return toast.error("Objet requis");
    if (kind === "VIDEO" && !videoUrl.trim()) return toast.error("Lien de la vidéo requis");
    setSaving(true);
    try {
      const { data } = await api.post("/activities/email", {
        company_id: company.id, destinataire: to, objet: objet.trim(), message: finalMessage,
        template_id: templateId || null,
      });
      if (data.resend_configured === false) toast.warning("Email SIMULÉ — configurez RESEND_API_KEY dans backend/.env pour un envoi réel");
      else toast.success(kind === "VIDEO" ? "Vidéo de démo envoyée" : "Email en cours d'envoi via Resend");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const plan = async () => {
    if (!title.trim()) return toast.error("Titre requis");
    // Un email planifié garde son brouillon : le jour venu, tout est prêt.
    const draft = email
      ? [dest.trim() && `Destinataire : ${dest.trim()}`,
         objet.trim() && `Objet : ${objet.trim()}`,
         (message.trim() || kind === "VIDEO") && finalMessage].filter(Boolean).join("\n")
      : "";
    const description = [notes.trim(), draft].filter(Boolean).join("\n\n") || null;
    setSaving(true);
    try {
      await api.post("/tasks", {
        company_id: company.id, title: title.trim(), type: kind, priority, description,
        due_date: date ? (time ? `${date}T${time}:00` : date) : null,
      });
      toast.success(DONE[kind]);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const suggestions = suggestedEmails.filter((e) => e && e !== dest.trim()).slice(0, 4);

  return (
    <Modal onClose={onClose} label="Planifier une action" className="max-w-[560px]" testid="plan-action-modal">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-[17px] text-slate-900">Planifier une action</h3>
        <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
      </div>

      {/* Le choix d'abord : la suite du formulaire en dépend. */}
      <div className="mt-3.5 grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Type d'action">
        {ACTION_TYPES.map(({ id, label }) => {
          const Icon = ICONS[id];
          const on = kind === id;
          return (
            <button key={id} type="button" role="radio" aria-checked={on} data-testid={`plan-kind-${id}`}
              onClick={() => pickKind(id)}
              className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-2.5 text-[12px] font-medium ${
                on ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              <Icon size={16} /> {label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 space-y-3">
        {email && (
          <div className="flex rounded-xl border border-slate-200 p-1 text-[13px]" role="tablist" aria-label="Quand">
            {[["now", "Envoyer maintenant"], ["later", "Planifier l'envoi"]].map(([m, l]) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} data-testid={`plan-mode-${m}`}
                disabled={m === "now" && Boolean(company.excluded)}
                onClick={() => setMode(m)}
                className={`flex-1 rounded-lg py-1.5 font-medium disabled:opacity-40 ${
                  mode === m ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
        {email && company.excluded && (
          <p className="text-[12px] text-rose-600">
            Cette entreprise ne souhaite plus être contactée : l'envoi est bloqué, seule la planification reste possible.
          </p>
        )}

        {!sendNow && (
          <>
            <label className="block">
              <span className={LABEL}>Titre</span>
              <input data-testid="plan-title" value={title} onChange={(e) => setTitle(e.target.value)}
                className={FIELD} placeholder="Ex. Démo à trois" />
            </label>
            <div className="grid grid-cols-[1fr_112px_1fr] gap-2">
              <label className="block">
                <span className={LABEL}>Date</span>
                <input type="date" data-testid="plan-date" value={date} onChange={(e) => setDate(e.target.value)}
                  className={`${FIELD} text-slate-600`} />
              </label>
              <label className="block">
                <span className={LABEL}>Heure</span>
                <input type="time" data-testid="plan-time" value={time} onChange={(e) => setTime(e.target.value)}
                  className={`${FIELD} text-slate-600`} />
              </label>
              <label className="block">
                <span className={LABEL}>Priorité</span>
                <select data-testid="plan-priority" value={priority} onChange={(e) => setPriority(e.target.value)}
                  className={`${FIELD} bg-white text-slate-600`}>
                  {[["low", "Basse"], ["medium", "Moyenne"], ["high", "Haute"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
            {!email && (
              <textarea data-testid="plan-notes" placeholder="Notes (optionnel)" value={notes}
                onChange={(e) => setNotes(e.target.value)} rows={2} className={`${FIELD} resize-y`} />
            )}
          </>
        )}

        {email && (
          <>
            <div>
              <span className={LABEL}>Destinataire</span>
              <input data-testid="plan-dest" placeholder="adresse@entreprise.fr" value={dest}
                onChange={(e) => setDest(e.target.value)} className={FIELD} />
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {suggestions.map((s) => (
                    <button key={s} type="button" onClick={() => setDest(s)}
                      className="text-[11.5px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {kind === "VIDEO" && (
              <label className="block">
                <span className={LABEL}>Lien de la vidéo</span>
                <div className="relative">
                  <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="plan-video-url" placeholder="https://…" value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)} className={`${FIELD} pl-9`} />
                </div>
                {!videoConfigured && (
                  <span className="block text-[11.5px] text-slate-400 mt-1">
                    Aucun lien enregistré dans Paramètres → Prospection : renseignez-le ici.
                  </span>
                )}
              </label>
            )}
            {templates.length > 0 && (
              <select data-testid="plan-template" value={templateId} onChange={(e) => applyTemplate(e.target.value)}
                className={`${FIELD} bg-white text-slate-600`}>
                <option value="">— Sans template —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <input data-testid="plan-objet" placeholder="Objet" value={objet} onChange={(e) => setObjet(e.target.value)} className={FIELD} />
            {preview ? (
              <div data-testid="plan-preview" className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3.5">
                <div className="text-sm font-semibold text-slate-900 mb-1">{rendered(objet)}</div>
                <div className="text-[13.5px] text-slate-600 whitespace-pre-line leading-relaxed">{rendered(finalMessage)}</div>
              </div>
            ) : (
              <textarea data-testid="plan-message" placeholder={"Message…  Variables : {nom} {ville} {commercial}"} value={message}
                onChange={(e) => setMessage(e.target.value)} rows={5} className={`${FIELD} resize-y leading-relaxed`} />
            )}
            <button type="button" data-testid="plan-preview-btn" onClick={() => setPreview((p) => !p)}
              className="text-[12.5px] font-medium text-indigo-600 hover:underline">
              {preview ? "Modifier le message" : "Aperçu du message"}
            </button>
          </>
        )}

        <div className="flex gap-2 pt-1.5">
          <button type="button" onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:border-slate-300">Annuler</button>
          <button type="button" data-testid="plan-save" onClick={sendNow ? send : plan} disabled={saving}
            className={`flex-1 py-2.5 ${BTN_PRIMARY}`}>
            {saving ? "Enregistrement…" : sendNow ? "Envoyer" : "Planifier"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
